import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Rocket, User, GraduationCap, MapPin, Target, CheckCircle2,
  Loader2, ArrowRight, ArrowLeft, Sparkles, Lock, ChevronRight, Plus, Trash2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEP_CONFIG = [
  {
    step: 1,
    title: 'Welcome to Eventism',
    subtitle: 'Your sales career starts here',
    icon: Rocket,
    color: 'from-indigo-500 to-violet-600',
    accent: 'text-indigo-400',
    bgAccent: 'bg-indigo-500/10',
  },
  {
    step: 2,
    title: 'Complete Your Profile',
    subtitle: 'First impressions matter',
    icon: User,
    color: 'from-emerald-500 to-teal-600',
    accent: 'text-emerald-400',
    bgAccent: 'bg-emerald-500/10',
  },
  {
    step: 3,
    title: 'Eventism University',
    subtitle: 'Training = Performance = Profits',
    icon: GraduationCap,
    color: 'from-amber-500 to-orange-600',
    accent: 'text-amber-400',
    bgAccent: 'bg-amber-500/10',
  },
];

function StepIndicator({ steps, currentStep, stepDetails }) {
  return (
    <div className="flex items-center gap-1 w-full max-w-md mx-auto" data-testid="step-indicator">
      {steps.map((s, idx) => {
        const detail = stepDetails?.find(d => d.step === s.step);
        const isCompleted = detail?.completed;
        const isCurrent = s.step === currentStep;
        return (
          <React.Fragment key={s.step}>
            <div className="flex flex-col items-center flex-shrink-0">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ${
                  isCompleted
                    ? 'bg-emerald-500 text-foreground'
                    : isCurrent
                    ? `bg-gradient-to-br ${s.color} text-foreground ring-2 ring-white/20 ring-offset-2 ring-offset-slate-950`
                    : 'bg-muted text-muted-foreground'
                }`}
                data-testid={`step-dot-${s.step}`}
              >
                {isCompleted ? <CheckCircle2 className="h-4 w-4" /> : s.step}
              </div>
              <span className={`text-[9px] mt-1 whitespace-nowrap ${isCurrent ? 'text-foreground font-semibold' : 'text-muted-foreground'}`}>
                {s.title.split(' ').slice(0, 2).join(' ')}
              </span>
            </div>
            {idx < steps.length - 1 && (
              <div className={`flex-1 h-0.5 rounded-full mx-1 transition-all duration-500 ${
                isCompleted ? 'bg-emerald-500' : 'bg-muted'
              }`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function WelcomeStep() {
  return (
    <div className="text-center max-w-xl mx-auto" data-testid="welcome-step">
      <div className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center mb-6 shadow-lg shadow-indigo-500/20">
        <Rocket className="h-10 w-10 text-foreground" />
      </div>
      <h2 className="text-3xl font-bold text-foreground mb-3">Welcome to the Eventism Sales Team</h2>
      <p className="text-muted-foreground mb-8 leading-relaxed">
        Congratulations on being approved! Complete 3 quick steps to unlock your Sales Dashboard.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-left">
        {[
          { icon: Sparkles, text: 'AI-powered sales tools', desc: 'Pitch generators, copilot, auto sequences' },
          { icon: Target, text: 'CRM & Pipeline', desc: 'Track leads from contact to close' },
          { icon: GraduationCap, text: 'Ongoing Training', desc: 'Eventism University keeps you sharp' },
          { icon: MapPin, text: 'Territory Management', desc: 'Own your market, grow your book' },
        ].map(item => (
          <div key={item.text} className="flex items-start gap-3 p-3 rounded-xl bg-muted/50 border border-border">
            <div className="w-8 h-8 rounded-lg bg-indigo-500/10 flex items-center justify-center flex-shrink-0">
              <item.icon className="h-4 w-4 text-indigo-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">{item.text}</p>
              <p className="text-xs text-muted-foreground">{item.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProfileStep({ profileData, setProfileData }) {
  return (
    <div className="max-w-lg mx-auto" data-testid="profile-step">
      <div className="text-center mb-6">
        <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center mb-4 shadow-lg shadow-emerald-500/20">
          <User className="h-8 w-8 text-foreground" />
        </div>
        <h2 className="text-2xl font-bold text-foreground mb-1">Complete Your Profile</h2>
        <p className="text-muted-foreground text-sm">Clients and partners will see this information.</p>
      </div>
      <div className="space-y-4">
        <div>
          <Label className="text-xs text-muted-foreground">Profile Photo URL</Label>
          <Input
            value={profileData.profile_photo_url}
            onChange={e => setProfileData(p => ({ ...p, profile_photo_url: e.target.value }))}
            placeholder="https://example.com/photo.jpg"
            className="bg-muted border-border h-10"
            data-testid="profile-photo-input"
          />
          <p className="text-[10px] text-muted-foreground mt-1">Paste a URL to your professional headshot</p>
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Phone Number *</Label>
          <Input
            value={profileData.phone}
            onChange={e => setProfileData(p => ({ ...p, phone: e.target.value }))}
            placeholder="(555) 123-4567"
            className="bg-muted border-border h-10"
            data-testid="profile-phone-input"
          />
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Professional Bio *</Label>
          <Textarea
            value={profileData.bio}
            onChange={e => setProfileData(p => ({ ...p, bio: e.target.value }))}
            placeholder="Tell prospects and team members about your experience and specialties..."
            className="bg-muted border-border min-h-[120px]"
            data-testid="profile-bio-input"
          />
          <p className="text-[10px] text-muted-foreground mt-1">{profileData.bio.length}/500 characters</p>
        </div>
      </div>
    </div>
  );
}

function CertificationStep({ onGoToUniversity }) {
  const [certStatus, setCertStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();

  const fetchCert = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/university/certification-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCertStatus(res.data);
    } catch { setCertStatus(null); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchCert(); }, [fetchCert]);

  if (loading) {
    return <div className="text-center py-12"><Loader2 className="h-6 w-6 animate-spin text-amber-400 mx-auto" /></div>;
  }

  const isCertified = certStatus?.certified;

  return (
    <div className="max-w-lg mx-auto text-center" data-testid="certification-step">
      <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center mb-4 shadow-lg shadow-amber-500/20">
        <GraduationCap className="h-8 w-8 text-foreground" />
      </div>
      <h2 className="text-2xl font-bold text-foreground mb-1">Eventism University Certification</h2>
      <p className="text-muted-foreground text-sm mb-6">Complete all mandatory training tracks to continue.</p>

      {isCertified ? (
        <div className="p-6 rounded-xl bg-emerald-500/5 border border-emerald-500/20" data-testid="cert-complete">
          <CheckCircle2 className="h-12 w-12 text-emerald-400 mx-auto mb-3" />
          <p className="text-lg font-semibold text-emerald-400 mb-1">Certification Complete!</p>
          <p className="text-sm text-muted-foreground mb-4">
            {certStatus.completed_tracks}/{certStatus.total_mandatory} mandatory tracks completed
          </p>
          <Button
            onClick={() => window.location.href = '/sales'}
            className="bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-semibold w-full"
            data-testid="go-to-dashboard-btn"
          >
            <CheckCircle2 className="h-4 w-4 mr-2" /> Go to Sales Dashboard
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="p-4 rounded-xl bg-muted/50 border border-border text-left">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Required Tracks</p>
            {certStatus?.missing_tracks?.map(t => (
              <div key={t.track_id} className="flex items-center gap-2 py-1.5">
                <Lock className="h-4 w-4 text-amber-400" />
                <span className="text-sm text-muted-foreground">{t.name}</span>
                <Badge className="ml-auto bg-amber-500/20 text-amber-400 text-[10px]">Incomplete</Badge>
              </div>
            ))}
            <div className="mt-3 pt-3 border-t border-border">
              <p className="text-xs text-muted-foreground">
                {certStatus?.completed_tracks}/{certStatus?.total_mandatory} mandatory tracks completed
              </p>
            </div>
          </div>
          <Button
            onClick={onGoToUniversity}
            className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold w-full"
            data-testid="go-to-eu-btn"
          >
            <GraduationCap className="h-4 w-4 mr-2" /> Go to Eventism University
          </Button>
          <p className="text-xs text-muted-foreground">Complete the training, then return here to continue.</p>
        </div>
      )}
    </div>
  );
}

function TerritoryStep({ territoryData, setTerritoryData }) {
  const addTerritory = () => {
    setTerritoryData(p => ({ ...p, territories: [...p.territories, ''] }));
  };

  const removeTerritory = (idx) => {
    setTerritoryData(p => ({
      ...p,
      territories: p.territories.filter((_, i) => i !== idx),
    }));
  };

  const updateTerritory = (idx, val) => {
    setTerritoryData(p => ({
      ...p,
      territories: p.territories.map((t, i) => (i === idx ? val : t)),
    }));
  };

  return (
    <div className="max-w-lg mx-auto" data-testid="territory-step">
      <div className="text-center mb-6">
        <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center mb-4 shadow-lg shadow-rose-500/20">
          <MapPin className="h-8 w-8 text-foreground" />
        </div>
        <h2 className="text-2xl font-bold text-foreground mb-1">Set Your Sales Territory</h2>
        <p className="text-muted-foreground text-sm">Define your primary market and coverage areas.</p>
      </div>
      <div className="space-y-4">
        <div>
          <Label className="text-xs text-muted-foreground">Primary Territory *</Label>
          <Input
            value={territoryData.primary_territory}
            onChange={e => setTerritoryData(p => ({ ...p, primary_territory: e.target.value }))}
            placeholder="e.g., Denver Metro Area, CO"
            className="bg-muted border-border h-10"
            data-testid="primary-territory-input"
          />
          <p className="text-[10px] text-muted-foreground mt-1">City, region, or zip code area where you'll focus</p>
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="text-xs text-muted-foreground">Additional Coverage Areas</Label>
            <Button size="sm" variant="ghost" className="h-6 text-xs text-indigo-400" onClick={addTerritory} data-testid="add-territory-btn">
              <Plus className="h-3 w-3 mr-1" /> Add
            </Button>
          </div>
          <div className="space-y-2">
            {territoryData.territories.map((t, idx) => (
              <div key={idx} className="flex gap-2">
                <Input
                  value={t}
                  onChange={e => updateTerritory(idx, e.target.value)}
                  placeholder="e.g., Boulder, CO"
                  className="bg-muted border-border h-9"
                  data-testid={`territory-input-${idx}`}
                />
                <Button size="sm" variant="ghost" className="h-9 w-9 p-0 text-red-400" onClick={() => removeTerritory(idx)}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function FirstOutreachStep({ onAddLead }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();

  useEffect(() => {
    const fetchLeads = async () => {
      try {
        const res = await axios.get(`${API}/sales/dashboard`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setLeads(res.data?.leads || []);
      } catch { setLeads([]); }
      finally { setLoading(false); }
    };
    fetchLeads();
  }, [token]);

  if (loading) {
    return <div className="text-center py-12"><Loader2 className="h-6 w-6 animate-spin text-cyan-400 mx-auto" /></div>;
  }

  const hasLeads = leads.length > 0;

  return (
    <div className="max-w-lg mx-auto text-center" data-testid="outreach-step">
      <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center mb-4 shadow-lg shadow-cyan-500/20">
        <Target className="h-8 w-8 text-foreground" />
      </div>
      <h2 className="text-2xl font-bold text-foreground mb-1">Your First Prospect</h2>
      <p className="text-muted-foreground text-sm mb-6">
        Add your first lead to kick off your sales pipeline.
      </p>

      {hasLeads ? (
        <div className="p-6 rounded-xl bg-emerald-500/5 border border-emerald-500/20" data-testid="lead-exists">
          <CheckCircle2 className="h-12 w-12 text-emerald-400 mx-auto mb-3" />
          <p className="text-lg font-semibold text-emerald-400 mb-1">Lead Added!</p>
          <p className="text-sm text-muted-foreground">
            You have {leads.length} lead{leads.length !== 1 ? 's' : ''} in your pipeline.
          </p>
          <div className="mt-4 space-y-2 text-left">
            {leads.slice(0, 3).map(l => (
              <div key={l.lead_id} className="flex items-center justify-between p-2.5 rounded-lg bg-muted/50">
                <div>
                  <p className="text-sm font-medium text-foreground">{l.client_name}</p>
                  <p className="text-[10px] text-muted-foreground">{l.event_type} | ${(l.budget || 0).toLocaleString()}</p>
                </div>
                <Badge className="bg-blue-500/20 text-blue-400 text-[10px]">{l.status}</Badge>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="p-4 rounded-xl bg-muted/50 border border-border">
            <p className="text-sm text-muted-foreground mb-4">
              Your pipeline is empty. Add a lead to complete this step.
            </p>
            <Button
              onClick={onAddLead}
              className="bg-cyan-500 hover:bg-cyan-600 text-slate-900 font-semibold w-full"
              data-testid="add-first-lead-btn"
            >
              <Plus className="h-4 w-4 mr-2" /> Add Your First Lead
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            You can also add leads later from your Sales Dashboard.
          </p>
        </div>
      )}
    </div>
  );
}

export default function SalesOnboardingWizard() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [onboardingData, setOnboardingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [activeStep, setActiveStep] = useState(1);

  // Profile step state
  const [profileData, setProfileData] = useState({
    profile_photo_url: '',
    phone: '',
    bio: '',
  });

  // Territory step state
  const [territoryData, setTerritoryData] = useState({
    primary_territory: '',
    territories: [],
  });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/onboarding/status`, { headers });
      setOnboardingData(res.data);
      if (res.data.completed) {
        navigate('/sales');
        return;
      }
      // Set active step to current
      setActiveStep(res.data.current_step || 1);
    } catch (err) {
      console.error('Failed to fetch onboarding status:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) fetchStatus();
  }, [token, fetchStatus]);

  // Pre-fill profile from user data
  useEffect(() => {
    if (user) {
      setProfileData(p => ({
        ...p,
        phone: user.phone || p.phone,
        bio: user.bio || p.bio,
        profile_photo_url: user.profile_photo_url || p.profile_photo_url,
      }));
    }
  }, [user]);

  const completeStep = async () => {
    setCompleting(true);
    try {
      let body = {};
      if (activeStep === 2) {
        body = profileData;
      } else if (activeStep === 4) {
        body = {
          primary_territory: territoryData.primary_territory,
          territories: territoryData.territories.filter(t => t.trim()),
        };
      }
      await axios.post(`${API}/onboarding/complete-step/${activeStep}`, body, {
        headers: { ...headers, 'Content-Type': 'application/json' },
      });
      toast.success(`Step ${activeStep} complete!`);
      await fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to complete step');
    } finally {
      setCompleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-indigo-400" />
      </div>
    );
  }

  if (!onboardingData || onboardingData.status === 'not_started') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Lock className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">Onboarding not available yet.</p>
          <Button onClick={() => navigate('/sales')} className="mt-4" variant="outline">Go Back</Button>
        </div>
      </div>
    );
  }

  const currentConfig = STEP_CONFIG[activeStep - 1];
  const stepDetails = onboardingData.step_details || [];
  const currentDetail = stepDetails.find(d => d.step === activeStep);
  const isStepCompleted = currentDetail?.completed;

  // Can proceed: step is completed OR we can complete it
  const canGoNext = activeStep < 3;
  const canGoBack = activeStep > 1;

  // Determine if the "Continue" button should be enabled
  let canComplete = false;
  if (activeStep === 1) canComplete = true;
  if (activeStep === 2) canComplete = profileData.bio.trim() && profileData.phone.trim();
  if (activeStep === 3) {
    const cert = stepDetails.find(d => d.step === 3);
    canComplete = cert?.completed;
  }

  const isLastStep = activeStep === 3;
  const allComplete = isStepCompleted && activeStep === 3;

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="sales-onboarding-wizard">
      {/* Top gradient accent */}
      <div className={`h-1 bg-gradient-to-r ${currentConfig.color}`} />

      {/* Header */}
      <div className="px-4 pt-6 pb-4 max-w-3xl mx-auto w-full">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-indigo-400" />
            <span className="text-sm font-semibold text-foreground">Sales Agent Onboarding</span>
          </div>
          <span className="text-xs text-muted-foreground">Step {activeStep} of 3</span>
        </div>
        <StepIndicator steps={STEP_CONFIG} currentStep={activeStep} stepDetails={stepDetails} />
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center px-4 pb-32">
        <div className="w-full max-w-3xl mx-auto">
          <Card className="bg-muted/40 border-border/50 backdrop-blur-sm">
            <CardContent className="p-8">
              {activeStep === 1 && <WelcomeStep />}
              {activeStep === 2 && <ProfileStep profileData={profileData} setProfileData={setProfileData} />}
              {activeStep === 3 && <CertificationStep onGoToUniversity={() => navigate('/university')} />}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Footer Navigation */}
      <div className="fixed bottom-0 left-0 right-0 border-t border-border bg-background/95 backdrop-blur-md">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            {canGoBack && isStepCompleted && (
              <Button
                variant="ghost"
                className="text-muted-foreground hover:text-foreground"
                onClick={() => setActiveStep(s => s - 1)}
                data-testid="onboarding-back-btn"
              >
                <ArrowLeft className="h-4 w-4 mr-1" /> Back
              </Button>
            )}
          </div>
          <div className="flex items-center gap-3">
            {isStepCompleted ? (
              isLastStep && allComplete ? (
                <Button
                  className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-foreground font-semibold px-8"
                  onClick={() => navigate('/sales')}
                  data-testid="onboarding-finish-btn"
                >
                  <CheckCircle2 className="h-4 w-4 mr-2" /> Go to Sales Dashboard
                </Button>
              ) : canGoNext ? (
                <Button
                  className={`bg-gradient-to-r ${currentConfig.color} text-foreground font-semibold px-6`}
                  onClick={() => setActiveStep(s => s + 1)}
                  data-testid="onboarding-next-btn"
                >
                  Next Step <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              ) : null
            ) : (
              <Button
                className={`bg-gradient-to-r ${currentConfig.color} text-foreground font-semibold px-6`}
                onClick={completeStep}
                disabled={completing || !canComplete}
                data-testid="onboarding-complete-step-btn"
              >
                {completing ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <ArrowRight className="h-4 w-4 mr-2" />
                )}
                {isLastStep ? 'Complete Onboarding' : 'Continue'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
